import React from "react";

import {
  citationFromClickTarget,
  openCitationInDocumentPanel,
} from "@/services/citationDeepLink";

interface CitationLinkInterceptorProps {
  children: React.ReactNode;
}

/**
 * Opens citation links clicked inside a rendered document in the document
 * panel instead of letting the browser navigate to them (#11164).
 *
 * A citation inside a chat-produced artifact is a real anchor carrying a real
 * absolute URL, because the delivered file has to resolve from Word or an
 * inbox where nothing can intercept it. Following that link while the document
 * is open in the app reloads the whole SPA and costs the user their place in
 * the conversation. So the link is left exactly as written and the *click* is
 * intercepted instead — the same trade the report editor makes in its
 * `citationLinkHandler` ProseMirror plugin.
 *
 * This is one delegated listener rather than a change per renderer: the file
 * viewer dispatches to a different component for each format, and the ones
 * that paint their links into this subtree — docx-as-HTML, markdown, html,
 * spreadsheet — are all covered at once, along with any format added later.
 *
 * It does NOT cover the PDF viewer, which renders inside an iframe whose
 * clicks never reach here; that path is handled by
 * `useCitationLinkInterception`, and it is the one most chat artifacts
 * actually take. Both share the same helpers so they can't drift apart.
 *
 * The wrapper is `display: contents`, so it takes part in event bubbling
 * without introducing a box that would disturb the viewer's layout.
 */
const CitationLinkInterceptor: React.FC<CitationLinkInterceptorProps> = ({
  children,
}) => {
  const handleClick = (event: React.MouseEvent<HTMLDivElement>): void => {
    const citation = citationFromClickTarget(event.target);
    if (!citation) {
      // Not a citation — an ordinary link, or no link at all. Leave it be.
      return;
    }

    event.preventDefault();
    openCitationInDocumentPanel(citation);
  };

  return (
    <div className="contents" onClick={handleClick}>
      {children}
    </div>
  );
};

export default CitationLinkInterceptor;
